import type { CSSProperties } from 'react';
import { forwardRef } from 'react';
import type {
  StrictIconProps,
  SemanticCOLORS,
  SemanticICONS,
} from 'semantic-ui-react';
import { Icon } from 'semantic-ui-react';

import type { HealthStatus } from '@models';

export type StatusVariant =
  'error' | 'info' | 'neutral' | 'success' | 'warning';

export const colorsByStatus: Record<StatusVariant, SemanticCOLORS> = {
  error: 'red',
  info: 'blue',
  neutral: 'grey',
  success: 'green',
  warning: 'yellow',
};

export const iconsByStatus: Record<StatusVariant, SemanticICONS> = {
  error: 'exclamation circle',
  info: 'info circle',
  neutral: 'question circle',
  success: 'check circle',
  warning: 'exclamation triangle',
};

export const healthStatusVariants: Record<HealthStatus, StatusVariant> = {
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'error',
};

export interface StatusIconProps extends StrictIconProps {
  /**
   * The type of status. Determines the color and icon, unless overridden.
   */
  variant?: StatusVariant;
  style?: CSSProperties;
}

/**
 * A convenience component to render a preset status-based icon.
 */
const StatusIcon = forwardRef<HTMLElement, StatusIconProps>(
  ({ name, variant = 'neutral', ...iconProps }, forwardedRef) => (
    <Icon
      ref={forwardedRef}
      role="presentation"
      name={name || iconsByStatus[variant]}
      color={colorsByStatus[variant]}
      {...iconProps}
    />
  ),
);
StatusIcon.displayName = 'StatusIcon';

export default StatusIcon;
